import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  IServicePartner,
  IServiceStatus,
  IServiceTicket,
} from "../../models/serviceManagement.model";
import {
  addServicePartnerApi,
  addServiceTicketApi,
  addTalukaApi,
  fetchInverterModelNo,
  fetchServiceErrorCodes,
  fetchServicePartners,
  fetchServicePartnersByFilter,
  fetchServiceStatuses,
  fetchServiceTicketsByStatus,
  updateServiceTicketApi,
  updateServiceTicketStatusApi,
  updateServicePartnerApi,
} from "../../api/serviceManagement.api";

interface ServiceManagementState {
  servivePartners: IServicePartner[];
  serviceTickets: IServiceTicket[];
  serviceStatuses: IServiceStatus[];
  statusTickets: Record<
    string,
    {
      tickets: IServiceTicket[];
      page: number;
      limit: number;
      hasMore: boolean;
      totalCount: number;
      loading: boolean;
    }
  >;
  inverterModelNumbers: {
    singlePhase: string[];
    threePhase: string[];
  };
  serviceErrorCodes: {
    singlePhase: Record<string, string>;
    threePhase: Record<string, string>;
  };
  loading: boolean;
  error?: string;
  loadingState: {
    addServicePartnerLoading: boolean;
    fetchServicePartnersLoading: boolean;
    addServiceTicketLoading: boolean;
    fetchServiceStatusesLoading: boolean;
    updateServiceTicketLoading: boolean;
    getInverterModelNoLoading: boolean;
    getServiceErrorCodesLoading: boolean;
    addTalukaLoading: boolean;
    updateServicePartnerLoading: boolean;
  };
}

const initialState: ServiceManagementState = {
  servivePartners: [],
  serviceTickets: [],
  serviceStatuses: [],
  statusTickets: {},
  inverterModelNumbers: {
    singlePhase: [] as string[],
    threePhase: [] as string[],
  },
  serviceErrorCodes: {
    singlePhase: {} as Record<string, string>,
    threePhase: {} as Record<string, string>,
  },
  loading: false,
  error: undefined,
  loadingState: {
    addServicePartnerLoading: false,
    fetchServicePartnersLoading: false,
    addServiceTicketLoading: false,
    fetchServiceStatusesLoading: false,
    updateServiceTicketLoading: false,
    getInverterModelNoLoading: false,
    getServiceErrorCodesLoading: false,
    addTalukaLoading: false,
    updateServicePartnerLoading: false,
  },
};

export const addServicePartner = createAsyncThunk(
  "serviceManagement/addServicePartner",
  async (servicePartner: IServicePartner) => {
    try {
      const response = await addServicePartnerApi(servicePartner);
      return response;
    } catch (error) {
      return (error as any).response?.data || "Failed to add service partner";
    }
  },
);

export const getServicePartners = createAsyncThunk(
  "serviceManagement/fetchServicePartners",
  async (
    filters:
      | {
          searchQuery?: string;
          district?: string;
          taluka?: string;
          pincode?: string;
        }
      | undefined,
  ) => {
    try {
      const response = await fetchServicePartners(filters);
      return response;
    } catch (error) {
      return (
        (error as any).response?.data || "Failed to fetch service partners"
      );
    }
  },
);

export const addServiceTicket = createAsyncThunk(
  "serviceManagement/addServiceTicket",
  async (serviceTicket: IServiceTicket) => {
    try {
      const response = await addServiceTicketApi(serviceTicket);
      return response;
    } catch (error) {
      return (error as any).response?.data || "Failed to add service partner";
    }
  },
);

export const getServicePartnersByFilter = createAsyncThunk(
  "serviceManagement/getServicePartnersByFilter",
  async (filters: { pincode?: string; district?: string; taluka?: string }) => {
    try {
      const response = await fetchServicePartnersByFilter(filters);
      return response;
    } catch (error) {
      return (
        (error as any).response?.data ||
        "Failed to fetch service partners by pincode"
      );
    }
  },
);

export const getServiceStatuses = createAsyncThunk(
  "serviceManagement/getServiceStatuses",
  async () => {
    try {
      const response = await fetchServiceStatuses();
      return response;
    } catch (error) {
      return (
        (error as any).response?.data || "Failed to fetch service statuses"
      );
    }
  },
);

export const getServiceTicketsByStatus = createAsyncThunk(
  "serviceManagement/getServiceTicketsByStatus",
  async (
    {
      status,
      page = 1,
      limit = 20,
    }: { status: string; page?: number; limit?: number },
    { rejectWithValue },
  ) => {
    try {
      const res = await fetchServiceTicketsByStatus(status, page, limit);
      return { ...res, status };
    } catch (err: any) {
      return rejectWithValue(
        err?.message || "Failed to fetch tickets by status",
      );
    }
  },
);

export const updateServiceTicketStatus = createAsyncThunk(
  "serviceManagement/updateServiceTicketStatus",
  async (
    ticketData: {
      currentStatus: string;
      destinationStatus: string;
      ticketId: string;
      destinationIndex: number;
      currentIndex: number;
    },
    { rejectWithValue },
  ) => {
    const {
      currentStatus,
      destinationStatus,
      ticketId,
      destinationIndex,
      currentIndex,
    } = ticketData;

    try {
      return await updateServiceTicketStatusApi(
        currentStatus,
        destinationStatus,
        ticketId,
        destinationIndex,
        currentIndex,
      );
    } catch (error: any) {
      console.error("Error updating service ticket status:", error);
      return rejectWithValue(
        error?.message || "Failed to update service ticket status.",
      );
    }
  },
);

export const updateServiceTicket = createAsyncThunk(
  "serviceManagement/updateServiceTicket",
  async (
    {
      ticketId,
      payload,
    }: { ticketId: string; payload: Partial<IServiceTicket> },
    { rejectWithValue },
  ) => {
    try {
      return await updateServiceTicketApi(ticketId, payload);
    } catch (error: any) {
      return rejectWithValue(
        error?.message || "Failed to update service ticket",
      );
    }
  },
);

export const getInverterModelNo = createAsyncThunk(
  "serviceManagement/getInverterModelNo",
  async () => {
    try {
      const response = await fetchInverterModelNo();
      return response;
    } catch (error) {
      return (
        (error as any).response?.data || "Failed to fetch service error codes"
      );
    }
  },
);
export const getServiceErrorCodes = createAsyncThunk(
  "serviceManagement/getServiceErrorCodes",
  async () => {
    try {
      const response = await fetchServiceErrorCodes();
      return response;
    } catch (error) {
      return (
        (error as any).response?.data || "Failed to fetch service error codes"
      );
    }
  },
);

export const addTaluka = createAsyncThunk(
  "states/addTaluka",
  async (
    { district, taluka }: { district: string; taluka: string },
    { rejectWithValue },
  ) => {
    try {
      return await addTalukaApi(district, taluka);
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const updateServicePartner = createAsyncThunk(
  "serviceManagement/updateServicePartner",
  async (
    { id, payload }: { id: string; payload: Partial<IServicePartner> },
    { rejectWithValue },
  ) => {
    try {
      return await updateServicePartnerApi(id, payload);
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

const ServiceManagementSlice = createSlice({
  name: "serviceManagement",
  initialState,
  reducers: {
    updateTicketStatusLocally: (
      state,
      action: PayloadAction<{
        ticketId: string;
        newStatus: string;
        sourceStatus: string;
        destinationIndex?: number;
      }>,
    ) => {
      const { ticketId, newStatus, sourceStatus, destinationIndex } =
        action.payload;

      const sourceColumn = state.statusTickets[sourceStatus];
      if (!sourceColumn) return;

      const ticketIndex = sourceColumn.tickets.findIndex(
        (t) => t._id === ticketId,
      );
      if (ticketIndex === -1) return;

      const ticket = sourceColumn.tickets[ticketIndex];

      // Remove from source
      sourceColumn.tickets.splice(ticketIndex, 1);
      sourceColumn.totalCount = Math.max(0, sourceColumn.totalCount - 1);

      // Add to destination
      if (!state.statusTickets[newStatus]) {
        state.statusTickets[newStatus] = {
          tickets: [],
          page: 1,
          limit: 20,
          hasMore: true,
          totalCount: 0,
          loading: false,
        };
      }

      const destColumn = state.statusTickets[newStatus];

      if (destinationIndex !== undefined && destinationIndex >= 0) {
        destColumn.tickets.splice(destinationIndex, 0, {
          ...ticket,
          status: newStatus,
        });
      } else {
        destColumn.tickets.unshift({
          ...ticket,
          status: newStatus,
        });
      }

      destColumn.totalCount = (destColumn.totalCount || 0) + 1;
    },

    reorderTicketsInColumn: (
      state,
      action: PayloadAction<{
        status: string;
        dragIndex: number;
        hoverIndex: number;
      }>,
    ) => {
      const { status, dragIndex, hoverIndex } = action.payload;
      const column = state.statusTickets[status];

      if (column && column.tickets.length > Math.max(dragIndex, hoverIndex)) {
        const [removed] = column.tickets.splice(dragIndex, 1);
        column.tickets.splice(hoverIndex, 0, removed);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addServicePartner.pending, (state) => {
        state.loadingState.addServicePartnerLoading = true;
      })
      .addCase(addServicePartner.fulfilled, (state, action) => {
        state.loadingState.addServicePartnerLoading = false;
        state.servivePartners.unshift(action.payload);
      })
      .addCase(addServicePartner.rejected, (state, action) => {
        state.loadingState.addServicePartnerLoading = false;
        state.error = action.payload as string;
      })
      .addCase(getServicePartners.pending, (state) => {
        state.loadingState.fetchServicePartnersLoading = true;
      })
      .addCase(getServicePartners.fulfilled, (state, action) => {
        state.loadingState.fetchServicePartnersLoading = false;
        state.servivePartners = action.payload;
      })
      .addCase(getServicePartners.rejected, (state, action) => {
        state.loadingState.fetchServicePartnersLoading = false;
        state.error = action.payload as string;
      })
      .addCase(addServiceTicket.pending, (state) => {
        state.loadingState.addServiceTicketLoading = true;
      })
      .addCase(addServiceTicket.fulfilled, (state, action) => {
        state.loadingState.addServiceTicketLoading = false;
        const status = action.payload.status;

        if (status) {
          if (!state.statusTickets[status]) {
            state.statusTickets[status] = {
              tickets: [],
              page: 1,
              limit: 20,
              hasMore: true,
              totalCount: 0,
              loading: false,
            };
          }

          state.statusTickets[status].tickets.unshift(action.payload);

          state.statusTickets[status].totalCount =
            (state.statusTickets[status].totalCount || 0) + 1;
        }
      })
      .addCase(addServiceTicket.rejected, (state, action) => {
        state.loadingState.addServiceTicketLoading = false;
        state.error = action.payload as string;
      })
      .addCase(getServiceStatuses.pending, (state) => {
        state.loadingState.fetchServiceStatusesLoading = true;
      })
      .addCase(getServiceStatuses.fulfilled, (state, action) => {
        state.loadingState.fetchServiceStatusesLoading = false;
        state.serviceStatuses = action.payload || [];
      })
      .addCase(getServiceStatuses.rejected, (state, action) => {
        state.loadingState.fetchServiceStatusesLoading = false;
        state.error = action.payload as string;
      })
      .addCase(getServiceTicketsByStatus.pending, (state, action) => {
        const status = action.meta.arg.status;
        const requestedPage = action.meta.arg.page || 1;

        if (!state.statusTickets[status]) {
          state.statusTickets[status] = {
            tickets: [],
            page: requestedPage,
            limit: action.meta.arg.limit || 20,
            hasMore: true,
            totalCount: 0,
            loading: true,
          };
        } else {
          state.statusTickets[status].loading = true;
        }
      })
      .addCase(getServiceTicketsByStatus.fulfilled, (state, action) => {
        const {
          status,
          tickets = [],
          totalCount = 0,
          page = 1,
          limit = 20,
          hasMore = false,
        } = action.payload;
        if (!state.statusTickets[status]) {
          state.statusTickets[status] = {
            tickets: [...tickets],
            page,
            limit,
            hasMore,
            totalCount,
            loading: false,
          };
        } else {
          const existing = state.statusTickets[status];

          if (page && page > 1) {
            // Append new tickets below existing ones, avoiding duplicates
            const existingIds = new Set(existing.tickets.map((t) => t._id));
            const newUnique = tickets.filter(
              (t: IServiceTicket) => !existingIds.has(t._id),
            );
            existing.tickets = [...existing.tickets, ...newUnique];
            existing.page = page;
            existing.limit = limit;
            existing.hasMore = hasMore;
            existing.totalCount = totalCount;
            existing.loading = false;
          } else {
            // page === 1 -> replace list (initial load / refresh)
            existing.tickets = [...tickets];
            existing.page = page;
            existing.limit = limit;
            existing.hasMore = hasMore;
            existing.totalCount = totalCount;
            existing.loading = false;
          }
        }
      })
      .addCase(getServiceTicketsByStatus.rejected, (state, action) => {
        const status = action.meta.arg.status;
        if (!state.statusTickets[status]) {
          state.statusTickets[status] = {
            tickets: [],
            page: 1,
            limit: action.meta.arg.limit || 20,
            hasMore: false,
            totalCount: 0,
            loading: false,
          };
        } else {
          state.statusTickets[status].loading = false;
        }
        state.error = action.payload as string;
      })
      .addCase(updateServiceTicket.pending, (state) => {
        state.loadingState.updateServiceTicketLoading = true;
      })
      .addCase(updateServiceTicket.fulfilled, (state, action) => {
        state.loadingState.updateServiceTicketLoading = false;
        const updatedTicket = action.payload;

        Object.keys(state.statusTickets).forEach((status) => {
          const column = state.statusTickets[status];
          const index = column.tickets.findIndex(
            (t) => t._id === updatedTicket._id,
          );
          if (index !== -1) {
            column.tickets[index] = updatedTicket;
          }
        });
      })
      .addCase(updateServiceTicket.rejected, (state, action) => {
        state.loadingState.updateServiceTicketLoading = false;
        state.error = action.payload as string;
      })
      .addCase(getInverterModelNo.pending, (state) => {
        state.loadingState.getInverterModelNoLoading = true;
      })
      .addCase(getInverterModelNo.fulfilled, (state, action) => {
        state.loadingState.getInverterModelNoLoading = false;
        state.inverterModelNumbers = action.payload;
      })
      .addCase(getInverterModelNo.rejected, (state, action) => {
        state.loadingState.getInverterModelNoLoading = false;
        state.error = action.payload as string;
      })
      .addCase(getServiceErrorCodes.pending, (state) => {
        state.loadingState.getServiceErrorCodesLoading = true;
      })
      .addCase(getServiceErrorCodes.fulfilled, (state, action) => {
        state.loadingState.getServiceErrorCodesLoading = false;
        state.serviceErrorCodes = action.payload;
      })
      .addCase(getServiceErrorCodes.rejected, (state, action) => {
        state.loadingState.getServiceErrorCodesLoading = false;
        state.error = action.payload as string;
      })
      .addCase(addTaluka.pending, (state) => {
        state.loadingState.addTalukaLoading = true;
      })
      .addCase(addTaluka.fulfilled, (state, action) => {
        state.loadingState.addTalukaLoading = false;
      })
      .addCase(addTaluka.rejected, (state, action) => {
        state.loadingState.addTalukaLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateServicePartner.pending, (state) => {
        state.loadingState.updateServicePartnerLoading = true;
      })
      .addCase(updateServicePartner.fulfilled, (state, action) => {
        state.loadingState.updateServicePartnerLoading = false;

        const updatedPartner = action.payload;

        const index = state.servivePartners.findIndex(
          (p) => p._id === updatedPartner._id,
        );

        if (index !== -1) {
          state.servivePartners[index] = updatedPartner;
        }
      })
      .addCase(updateServicePartner.rejected, (state, action) => {
        state.loadingState.updateServicePartnerLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { updateTicketStatusLocally, reorderTicketsInColumn } =
  ServiceManagementSlice.actions;

export default ServiceManagementSlice.reducer;
